import { useMemo } from "react";
import { useSelector } from "react-redux";
import { getMenusData } from "@redux/menus/selector";
import { isEqual } from "@util/utils";

/**
 * 自定義 Hook：控制和過濾菜單清單（支援遞迴處理深層選單）
 * @param {Array} sourceData - 資料來源陣列
 * @param {string} [key='frontKey'] - 用於匹配的屬性名稱
 * @returns {Array} 過濾後的菜單清單（包含後端 isVisible 與可操作旗標）
 */
const useMenusControlled = (sourceData, key = "frontKey") => {
	const { menulist: menuList = [] } = useSelector(getMenusData) || {};
	const hostname = window.location.hostname;

	/**
	 * 遞迴過濾函數：處理深層選單結構
	 * @param {Object} item - 選單項目
	 * @returns {Object|null} 過濾後的選單項目，無權限則返回 null
	 */
	const filterMenuRecursive = item => {
		if (!item || !item[key]) return null;

		const frontKey = item[key];
		const isExist = menuList.some(menu => menu[key] === frontKey);
		const isDevBypass = /^CXX99[\d]+$/g.test(frontKey) && isEqual(hostname, "localhost");

		// 檢查當前項目是否有權限
		const hasPermission = isExist || isDevBypass;
		// 後端可見性資訊外拋給外層，Hook 不直接依此過濾
		const isVisible = item?.isVisible !== false;

		// 如果有 children，遞迴處理
		if (Array.isArray(item.children) && item.children.length > 0) {
			const filteredChildren = item.children.map(filterMenuRecursive).filter(Boolean); // 過濾掉 null

			// 如果父選單有權限，或子選單有權限，則保留
			if (hasPermission || filteredChildren.length > 0) {
				return {
					...item,
					isVisible,
					isAuthorized: hasPermission,
					isOperable: hasPermission && isVisible,
					children: filteredChildren.length > 0 ? filteredChildren : undefined,
				};
			}
			return null;
		}

		// 沒有 children，直接檢查權限
		if (!hasPermission) return null;

		return {
			...item,
			isVisible,
			isAuthorized: hasPermission,
			isOperable: hasPermission && isVisible,
		};
	};

	return useMemo(
		() => {
			if (!sourceData?.length || !key) return [];

			// 如果沒有 menuList，在開發環境下允許 CXX99 開頭的 frontKey 通過
			if (!menuList.length) {
				return sourceData.map(filterMenuRecursive).filter(Boolean);
			}

			return sourceData.map(filterMenuRecursive).filter(Boolean);
		},
		// eslint-disable-next-line
		[menuList, sourceData, key, hostname],
	);
};

export default useMenusControlled;
